import { Component, computed, inject, input, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { Order, OrderStatus } from '../../../../../models/order.model';
import { OrderService } from '../../../../../services/order.service';
import { TagStatusComponent } from '../../../../../../shared/components/tag-status/tag-status.component';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { DatePipe } from '@angular/common';

@Component({
  selector: 'app-order-details-contract',
  imports: [TagStatusComponent, DatePipe, ButtonModule, DialogModule],
  templateUrl: './order-details-contract.component.html',
})
export class OrderDetailsContractComponent {
  order = input<Order | null>(null);
  statusText = input<string | null>(null);

  private sanitizer = inject(DomSanitizer);
  private orderService = inject(OrderService);
  private messageService = inject(MessageService);

  reminderDialogVisible = signal(false);
  sendingReminder = signal(false);

  canSendReminder = computed(() => {
    const order = this.order();
    if (!order?.contract) return false;

    return order.status === OrderStatus.ContractPending;
  });

  contractTag = computed(() =>
    this.orderService.getContractStatusTag(this.order()?.contract),
  );

  contractSigned = computed(() =>
    this.orderService.isContractSigned(this.order()?.contract),
  );

  contractViewerUrl = computed<SafeResourceUrl | null>(() => {
    const url = this.contractUrl();
    if (!url) return null;

    return this.sanitizer.bypassSecurityTrustResourceUrl(
      `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`,
    );
  });

  openReminderDialog(): void {
    this.reminderDialogVisible.set(true);
  }

  closeReminderDialog(): void {
    if (this.sendingReminder()) return;
    this.reminderDialogVisible.set(false);
  }

  sendReminder(): void {
    const order = this.order();
    if (!order || this.sendingReminder()) return;

    this.sendingReminder.set(true);

    this.orderService.sendContractReminder(order.id).subscribe({
      next: () => {
        this.sendingReminder.set(false);
        this.reminderDialogVisible.set(false);
        this.messageService.add({
          severity: 'success',
          summary: 'Sucesso',
          detail: 'Lembrete de assinatura enviado ao cliente.',
          life: 3000,
        });
      },
      error: (e) => {
        this.sendingReminder.set(false);
        this.reminderDialogVisible.set(false);
        this.messageService.add({
          severity: 'error',
          summary: 'Erro',
          detail: this.reminderErrorMessage(e),
          life: 3000,
        });
      },
    });
  }

  private contractUrl = computed(
    () => this.order()?.contract?.contractUrl ?? null,
  );

  private reminderErrorMessage(e: unknown): string {
    const status = e instanceof HttpErrorResponse ? e.status : 0;

    if (status === 404) return 'Pedido ou contrato não encontrado.';
    if (status === 422) {
      return 'O pedido não está aguardando a assinatura do contrato.';
    }
    return 'Não foi possível enviar o lembrete. Tente novamente.';
  }
}
